import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTeam, fetchAllTeams } from "../lib/api-client";
import { Link } from "react-router-dom";
import type { TeamSummary } from "../types/api";

function AdminTeamsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const teamsQuery = useQuery({
    queryKey: ["all-teams"],
    queryFn: fetchAllTeams,
  });

  const [createForm, setCreateForm] = useState({ name: "", slug: "", planTier: "free" });

  const createTeamMutation = useMutation({
    mutationFn: async () => {
      if (!createForm.name) throw new Error("Team name is required");
      return createTeam({
        name: createForm.name,
        slug: createForm.slug || null,
        planTier: createForm.planTier || null,
      });
    },
    onSuccess() {
      setCreateForm({ name: "", slug: "", planTier: "free" });
      setShowCreateDialog(false);
      void queryClient.invalidateQueries({ queryKey: ["all-teams"] });
      setToast({ type: "success", message: "Team created successfully." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to create team.",
      });
    },
  });

  const teams = teamsQuery.data?.teams ?? [];
  const filteredTeams = teams.filter((team) =>
    team.name.toLowerCase().includes(search.toLowerCase()) ||
    (team.slug && team.slug.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--color-bg)" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.5rem", margin: 0, fontWeight: 600 }}>All Teams</h1>
          <p style={{ margin: "0.25rem 0 0 0", color: "var(--color-text-muted)" }}>
            Global team management across the platform.
          </p>
        </div>
        <button
          type="button"
          className="button primary"
          onClick={() => setShowCreateDialog(true)}
        >
          Create Team
        </button>
      </header>

      <main style={{ padding: "2rem", flex: 1, maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
        <div className="surface" style={{ padding: "1.5rem", borderRadius: "8px", boxShadow: "var(--shadow-sm)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
            <div style={{ position: "relative", width: "300px" }}>
              <input
                type="text"
                placeholder="Search teams..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.6rem 1rem",
                  borderRadius: "6px",
                  border: "1px solid var(--color-border)",
                  fontSize: "0.9rem",
                }}
              />
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--color-border)", textAlign: "left", color: "var(--color-text-muted)" }}>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Name</th>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Slug</th>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Plan Tier</th>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teamsQuery.isLoading ? (
                <tr>
                  <td colSpan={4} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                    Loading teams...
                  </td>
                </tr>
              ) : filteredTeams.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                    No teams found matching your search.
                  </td>
                </tr>
              ) : (
                filteredTeams.map((team) => (
                  <tr key={team.id} style={{ borderBottom: "1px solid var(--color-border-light)", transition: "background 0.2s" }}>
                    <td style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>
                      {team.name}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontFamily: "monospace" }}>
                      {team.slug || "—"}
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      <PlanBadge plan={team.planTier} />
                    </td>
                    <td style={{ padding: "0.75rem 1rem", textAlign: "right" }}>
                      {/* Note: We link to the team dashboard or settings. Assuming /teams/:id maps to dashboard */}
                      <Link
                        to={`/teams/${team.id}`}
                        className="button ghost"
                        style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem", textDecoration: "none" }}
                      >
                        Manage
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div style={{ marginTop: "1rem", color: "var(--color-text-muted)", fontSize: "0.85rem", textAlign: "center" }}>
            Showing {filteredTeams.length} teams
          </div>
        </div>
      </main>

      {/* Create Team Dialog */}
      {showCreateDialog && (
        <Dialog
          title="Create New Team"
          onClose={() => setShowCreateDialog(false)}
          onSubmit={() => createTeamMutation.mutateAsync()}
          isLoading={createTeamMutation.isPending}
        >
          <div className="form-group" style={{ display: "grid", gap: "1rem" }}>
            <label className="field">
              <span>Team Name</span>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Startups Inc."
                required
                className="input"
              />
            </label>
            <label className="field">
              <span>Team Slug (Optional)</span>
              <input
                type="text"
                value={createForm.slug}
                onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value })}
                placeholder="startups-inc"
                className="input"
              />
            </label>
            <label className="field">
              <span>Plan Tier</span>
              <select
                value={createForm.planTier}
                onChange={(e) => setCreateForm({ ...createForm, planTier: e.target.value })}
                className="select"
              >
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </label>
          </div>
        </Dialog>
      )}

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            background: toast.type === "success" ? "#10B981" : "#EF4444",
            color: "white",
            padding: "0.8rem 1.5rem",
            borderRadius: "6px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            zIndex: 2000,
            fontWeight: 500,
            animation: "slideIn 0.3s ease-out",
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const styles: Record<string, { bg: string; color: string }> = {
    free: { bg: "#F3F4F6", color: "#374151" }, // Gray
    pro: { bg: "#DBEAFE", color: "#1E40AF" }, // Blue
    enterprise: { bg: "#F3E8FF", color: "#6B21A8" }, // Purple
    standard: { bg: "#DBEAFE", color: "#1E40AF" }, // Blue fallback
  };

  const style = styles[plan] || styles.free;

  return (
    <span
      style={{
        background: style.bg,
        color: style.color,
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    >
      {plan}
    </span>
  );
}

function Dialog({
  title,
  onClose,
  onSubmit,
  isLoading,
  children,
  width = "450px",
}: {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  children: React.ReactNode;
  width?: string;
}): JSX.Element {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(2px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: "8px",
          padding: "0",
          width: width,
          maxWidth: "95vw",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          animation: "scaleIn 0.2s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "1.5rem", borderBottom: "1px solid var(--color-border)" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>{title}</h2>
        </div>

        <div style={{ padding: "1.5rem", overflowY: "auto" }}>
          <form
            id="dialog-form"
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmit();
            }}
          >
            {children}
          </form>
        </div>

        <div style={{ padding: "1rem 1.5rem", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: "0.75rem", background: "var(--color-bg-soft)", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}>
          <button type="button" className="button ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="dialog-form" className="button primary" disabled={isLoading}>
            {isLoading ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminTeamsPage;
